import { useState, createContext, useContext, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Upload, ChevronRight, ChevronLeft, Github, Shield, Figma, Database, CreditCard, CheckCircle, AlertCircle, Loader, Cloud } from 'lucide-react';
import AwsDrawer from './drawers/AwsDrawer';
import GcpDrawer from './drawers/GcpDrawer';
import GithubConnectModal from './modals/GithubConnectModal';
import evidenceService from '../services/evidenceService';
import assessmentService from '../services/assessmentService';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';

// Create context for wizard state
const WizardContext = createContext();

const STEPS = [
  { id: 1, title: 'Scope', description: 'Choose what this assessment will cover.' },
  { id: 2, title: 'Evidence', description: 'Upload optional artefacts or rely solely on the agent.' },
  { id: 3, title: 'Integrations', description: 'Power real-time AI tool tracking.' },
  { id: 4, title: 'Governance', description: 'Quick self-report for governance maturity.' },
  { id: 5, title: 'Review & Launch', description: 'Confirm details, then run your assessment.' },
];

export default function AssessmentWizard({ isOpen, onClose }) {
  const [currentStep, setCurrentStep] = useState(1);
  const [wizardState, setWizardState] = useState({
    scope: 'entire',
    businessUnit: '',
    evidenceMode: 'agent',
    files: [],
    pat: 'ghp_****************',
    evidenceSubmitted: false,
    integrations: {
      github: false,
      okta: false,
      figma: false,
      sbom: false,
      expense: false,
      gcp: false,
      aws: false
    },
    governance: {
      riskRegister: true,
      ethicsBoard: true,
      annualAudit: false,
      postIncidentReview: false,
      nistAiRmf: false
    },
    loading: false,
    result: null
  });

  const [isDraftSaved, setIsDraftSaved] = useState(false);
  const [isAwsDrawerOpen, setIsAwsDrawerOpen] = useState(false);
  const [isGcpDrawerOpen, setIsGcpDrawerOpen] = useState(false);
  const [isGithubModalOpen, setIsGithubModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleClose = () => {
    setIsDraftSaved(true);
    onClose();
  };

  const handleNext = () => {
    if (currentStep < STEPS.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const isNextDisabled = () => {
    if (currentStep === 2 && wizardState.evidenceMode === 'upload') {
      return !wizardState.evidenceSubmitted;
    }
    return false;
  };

  const handleRunAssessment = async () => {
    try {
      setIsLoading(true);
      await assessmentService.runAssessment();
      toast.success('Assessment has been queued successfully!');
      navigate('/dashboard'); // Redirect to dashboard
    } catch (error) {
      console.error('Error running assessment:', error);
      toast.error('Failed to queue assessment. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAwsConnected = () => {
    setWizardState(prev => ({
      ...prev,
      integrations: { ...prev.integrations, aws: true }
    }));
  };

  const handleGcpConnected = () => {
    setWizardState(prev => ({
      ...prev,
      integrations: { ...prev.integrations, gcp: true }
    }));
  };

  const handleGithubConnected = () => {
    setWizardState(prev => ({
      ...prev,
      integrations: { ...prev.integrations, github: true }
    }));
    // Store the GitHub connection state in localStorage
    localStorage.setItem('githubConnected', 'true');
  };

  const handleGithubDisconnect = () => {
    setWizardState(prev => ({
      ...prev,
      integrations: { ...prev.integrations, github: false }
    }));
    // Remove the GitHub connection state from localStorage
    localStorage.removeItem('githubConnected');
  };

  // Check for existing GitHub connection on component mount
  useEffect(() => {
    const isGithubConnected = localStorage.getItem('githubConnected') === 'true';
    if (isGithubConnected) {
      setWizardState(prev => ({
        ...prev,
        integrations: { ...prev.integrations, github: true }
      }));
    }
  }, []);

  if (!isOpen) return null;

  return (
    <WizardContext.Provider value={{ wizardState, setWizardState }}>
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50">
        <div className="fixed inset-0 pointer-events-none" onClick={handleClose} />
        
        <div className="absolute right-0 top-0 h-full w-[calc(100%-320px)] bg-background border-l border-primary/20">
          {/* Header */}
          <div className="sticky top-0 bg-background border-b border-primary/20 p-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-semibold">Start New Assessment</h2>
                <p className="text-text-secondary mt-1">{STEPS[currentStep - 1].description}</p>
              </div>
              <button
                onClick={handleClose}
                className="p-2 hover:bg-primary/10 rounded-lg transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            {/* Progress Bar */}
            <div className="mt-8 flex items-center gap-2">
              {STEPS.map((step, index) => (
                <div key={step.id} className="flex items-center">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                      currentStep >= step.id ? 'bg-primary text-background' : 'bg-background-light text-text-secondary'
                    }`}
                  >
                    {step.id}
                  </div>
                  {index < STEPS.length - 1 && (
                    <div
                      className={`h-1 w-16 mx-2 rounded transition-colors ${
                        currentStep > step.id ? 'bg-primary' : 'bg-background-light'
                      }`}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="p-6 pb-32 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 200px)' }}>
            <AnimatePresence mode="wait">
              <motion.div
                key={currentStep}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.2 }}
              >
                {currentStep === 1 && <ScopeStep />}
                {currentStep === 2 && <EvidenceStep />}
                {currentStep === 3 && <IntegrationsStep />}
                {currentStep === 4 && <GovernanceStep />}
                {currentStep === 5 && <ReviewStep onRunAssessment={handleRunAssessment} />}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Footer */}
          <div className="fixed bottom-0 left-[320px] right-0 p-6 border-t border-primary/20 bg-background">
            <div className="flex justify-between items-center">
              {currentStep > 1 ? (
                <button
                  onClick={handleBack}
                  className="flex items-center gap-2 px-4 py-2 hover:bg-primary/10 rounded-lg transition-colors"
                >
                  <ChevronLeft size={20} />
                  Back
                </button>
              ) : (
                <div />
              )}
              {currentStep < STEPS.length ? (
                <button
                  onClick={handleNext}
                  disabled={isNextDisabled()}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                    isNextDisabled()
                      ? 'bg-primary/20 text-text-secondary cursor-not-allowed'
                      : 'bg-primary text-background hover:bg-primary-light'
                  }`}
                >
                  Next
                  <ChevronRight size={20} />
                </button>
              ) : (
                <button
                  onClick={handleRunAssessment}
                  className="flex items-center gap-2 px-6 py-3 bg-primary text-background rounded-lg hover:bg-primary-light transition-colors"
                >
                  Run Assessment
                  <ChevronRight size={20} />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Draft Saved Toast */}
      <AnimatePresence>
        {isDraftSaved && (
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
            className="fixed bottom-4 right-4 bg-primary text-background px-4 py-2 rounded-lg shadow-lg"
          >
            Draft saved—return anytime.
          </motion.div>
        )}
      </AnimatePresence>

      <AwsDrawer
        isOpen={isAwsDrawerOpen}
        onClose={() => setIsAwsDrawerOpen(false)}
        onConnected={handleAwsConnected}
      />

      <GcpDrawer
        isOpen={isGcpDrawerOpen}
        onClose={() => setIsGcpDrawerOpen(false)}
        onConnected={handleGcpConnected}
      />

      <GithubConnectModal
        isOpen={isGithubModalOpen}
        onClose={() => setIsGithubModalOpen(false)}
        onConnected={handleGithubConnected}
      />
    </WizardContext.Provider>
  );
}

function ScopeStep() {
  const { wizardState, setWizardState } = useContext(WizardContext);

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <label className="block">
          <input
            type="radio"
            checked={wizardState.scope === 'entire'}
            onChange={() => setWizardState(prev => ({ ...prev, scope: 'entire' }))}
            className="mr-2"
          />
          Entire organisation
        </label>
        <label className="block">
          <input
            type="radio"
            checked={wizardState.scope === 'unit'}
            onChange={() => setWizardState(prev => ({ ...prev, scope: 'unit' }))}
            className="mr-2"
          />
          Specific business unit
        </label>
      </div>

      {wizardState.scope === 'unit' && (
        <select
          value={wizardState.businessUnit}
          onChange={(e) => setWizardState(prev => ({ ...prev, businessUnit: e.target.value }))}
          className="w-full bg-background-light border border-primary/20 rounded-lg px-4 py-2"
        >
          <option value="">Select a business unit</option>
          <option value="payments">Payments Division</option>
          <option value="rd">R&D</option>
          <option value="support">Support</option>
        </select>
      )}
    </div>
  );
}

function EvidenceStep() {
  const { wizardState, setWizardState } = useContext(WizardContext);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);

  const handleFileUpload = (files, dimension, documentType) => {
    setWizardState(prev => ({
      ...prev,
      files: [...prev.files, ...Array.from(files).map(file => ({ 
        name: file.name,
        dimension,
        documentType,
        file: file
      }))]
    }));
  };

  const handleSubmitEvidence = async () => {
    if (wizardState.files.length === 0) {
      setError('Please add at least one file before submitting');
      return;
    }

    setUploading(true);
    setError(null);
    
    try {
      await evidenceService.submitEvidence(wizardState.files);
      
      // Clear files and mark evidence as submitted
      setWizardState(prev => ({
        ...prev,
        files: [],
        evidenceSubmitted: true
      }));
      
      // Show success message
      alert('Evidence submitted successfully!');
    } catch (error) {
      console.error('Error submitting evidence:', error);
      setError(error.response?.data?.message || 'Failed to submit evidence. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const getAcceptedFormats = (formats) => {
    const formatMap = {
      'PDF': '.pdf',
      'DOCX': '.docx',
      'XLSX': '.xlsx',
      'CSV': '.csv',
      'JSON': '.json',
      'Markdown': '.md',
      'CycloneDX JSON': '.cyclonedx',
      'SPDX': '.spdx',
      'JSONL': '.jsonl'
    };
    return formats.map(f => formatMap[f]).join(',');
  };

  const evidenceOptions = [
    {
      dimension: 'Regulatory & Governance',
      options: [
        {
          name: 'AI Policy / Responsible-AI Charter',
          formats: ['PDF', 'DOCX'],
          extraction: 'keyword hits for EU AI Act articles, NIST functions, governance flags'
        },
        {
          name: 'Risk Register',
          formats: ['XLSX', 'CSV'],
          extraction: 'enterprise risk documentation and analysis'
        },
        {
          name: 'AI Impact Assessment',
          formats: ['PDF', 'DOCX'],
          extraction: 'EU draft, UNESCO, and other impact assessment frameworks'
        },
        {
          name: 'Board / Ethics-Committee minutes',
          formats: ['PDF', 'DOCX'],
          extraction: 'governance decisions and ethical considerations'
        }
      ]
    },
    {
      dimension: 'Privacy & Data Quality',
      options: [
        {
          name: 'Data Protection Impact Assessment (DPIA)',
          formats: ['PDF', 'DOCX'],
          extraction: 'GDPR compliance and data protection measures'
        },
        {
          name: 'GDPR Record of Processing Activities',
          formats: ['XLSX', 'CSV'],
          extraction: 'data processing documentation and compliance'
        },
        {
          name: 'Dataset documentation / Datasheets',
          formats: ['PDF', 'JSON'],
          extraction: 'dataset characteristics and quality metrics'
        },
        {
          name: 'Data-retention schedule',
          formats: ['XLSX', 'CSV'],
          extraction: 'retention periods and data lifecycle management'
        }
      ]
    },
    {
      dimension: 'Security (Software & Infra)',
      options: [
        {
          name: 'SBOM (Software Bill of Materials)',
          formats: ['CycloneDX JSON', 'SPDX'],
          extraction: 'software component inventory and dependencies'
        },
        {
          name: 'Vulnerability scan reports',
          formats: ['CSV', 'PDF'],
          extraction: 'security vulnerabilities and risk assessment'
        },
        {
          name: 'Pen-test / Red-team report',
          formats: ['PDF'],
          extraction: 'security testing results and recommendations'
        },
        {
          name: 'Cloud audit logs excerpt',
          formats: ['JSONL'],
          extraction: 'cloud activity and security events'
        }
      ]
    },
    {
      dimension: 'Incidents & Post-Mortems',
      options: [
        {
          name: 'Incident Report / RCA',
          formats: ['PDF', 'Markdown'],
          extraction: 'incident details and root cause analysis'
        },
        {
          name: 'Post-Incident Review Template',
          formats: ['PDF', 'Markdown'],
          extraction: 'incident response and improvement actions'
        }
      ]
    },
    {
      dimension: 'AI Model Transparency',
      options: [
        {
          name: 'Model Card',
          formats: ['Markdown', 'PDF'],
          extraction: 'model characteristics and performance metrics'
        },
        {
          name: 'System Card / Datasheet',
          formats: ['Markdown', 'PDF'],
          extraction: 'system documentation and capabilities'
        },
        {
          name: 'Bias & Fairness Test Report',
          formats: ['PDF', 'JSON'],
          extraction: 'bias assessment and fairness metrics'
        }
      ]
    }
  ];

  return (
    <div className="flex flex-col h-[calc(100vh-200px)]">
      <div className="flex gap-4 p-2 bg-background-light rounded-lg">
        <button
          className={`flex-1 px-4 py-2 rounded-lg transition-colors ${
            wizardState.evidenceMode === 'agent' ? 'bg-primary text-background' : 'hover:bg-primary/10'
          }`}
          onClick={() => setWizardState(prev => ({ ...prev, evidenceMode: 'agent' }))}
        >
          Agent data only
        </button>
        <button
          className={`flex-1 px-4 py-2 rounded-lg transition-colors ${
            wizardState.evidenceMode === 'upload' ? 'bg-primary text-background' : 'hover:bg-primary/10'
          }`}
          onClick={() => setWizardState(prev => ({ ...prev, evidenceMode: 'upload' }))}
        >
          Upload extra evidence
        </button>
      </div>

      {wizardState.evidenceMode === 'upload' && (
        <div className="flex flex-col flex-1 overflow-hidden">
          <div className="flex-1 overflow-y-auto pr-2">
            <div className="space-y-6">
              {evidenceOptions.map((section, index) => (
                <div key={index} className="bg-background-light rounded-lg p-4">
                  <h3 className="font-medium mb-4">{section.dimension}</h3>
                  <div className="space-y-4">
                    {section.options.map((option, optionIndex) => (
                      <div key={optionIndex} className="space-y-2">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <span className="font-medium">{option.name}</span>
                            <span className="text-sm text-text-secondary ml-2">
                              ({option.formats.join(', ')})
                            </span>
                          </div>
                          <label className="px-3 py-1 bg-primary/10 text-primary rounded-lg text-sm hover:bg-primary/20 transition-colors cursor-pointer">
                            Add Files
                            <input
                              type="file"
                              className="hidden"
                              accept={getAcceptedFormats(option.formats)}
                              multiple
                              onChange={(e) => handleFileUpload(e.target.files, section.dimension, option.name)}
                            />
                          </label>
                        </div>
                        <p className="text-sm text-text-secondary">
                          {option.extraction}
                        </p>
                        {wizardState.files.filter(f => f.documentType === option.name).length > 0 && (
                          <div className="mt-2 space-y-1">
                            {wizardState.files
                              .filter(f => f.documentType === option.name)
                              .map((file, fileIndex) => (
                                <div key={fileIndex} className="flex items-center justify-between p-2 bg-background rounded-lg">
                                  <span className="text-sm">{file.name}</span>
                                  <button
                                    onClick={() => setWizardState(prev => ({
                                      ...prev,
                                      files: prev.files.filter((_, i) => i !== prev.files.indexOf(file))
                                    }))}
                                    className="text-text-secondary hover:text-danger transition-colors"
                                  >
                                    <X size={16} />
                                  </button>
                                </div>
                              ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {error && (
              <div className="mt-4 p-4 bg-danger/10 text-danger rounded-lg">
                {error}
              </div>
            )}
          </div>

          <div className="bg-background border-t border-primary/20 p-4 mt-4">
            <button
              onClick={handleSubmitEvidence}
              disabled={wizardState.files.length === 0 || uploading}
              className={`w-full px-6 py-2 rounded-lg transition-colors ${
                wizardState.files.length === 0 || uploading
                  ? 'bg-primary/20 text-text-secondary cursor-not-allowed'
                  : 'bg-primary text-background hover:bg-primary-light'
              }`}
            >
              {uploading ? (
                <div className="flex items-center justify-center gap-2">
                  <Loader className="animate-spin" size={16} />
                  Submitting...
                </div>
              ) : (
                'Submit All Evidence'
              )}
            </button>
          </div>
        </div>
      )}

      <div className="bg-background-light rounded-lg p-4 mt-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-medium">Personal Access Token</h3>
            <p className="text-sm text-text-secondary">Read-only; used to detect AI code-review bots.</p>
          </div>
          <input
            type="password"
            value={wizardState.pat}
            disabled
            className="bg-background border border-primary/20 rounded-lg px-4 py-2"
          />
        </div>
      </div>
    </div>
  );
}

function IntegrationsStep() {
  const { wizardState, setWizardState } = useContext(WizardContext);
  const [isAwsDrawerOpen, setIsAwsDrawerOpen] = useState(false);
  const [isGcpDrawerOpen, setIsGcpDrawerOpen] = useState(false);
  const [isGithubModalOpen, setIsGithubModalOpen] = useState(false);

  const integrations = [
    { key: 'github', name: 'GitHub', icon: Github, description: 'Connect to analyze code review patterns', available: false },
    { key: 'okta', name: 'Okta', icon: Shield, description: 'Track AI tool access management', available: false },
    { key: 'figma', name: 'Figma', icon: Figma, description: 'Monitor design system automation', available: false },
    { key: 'sbom', name: 'SBOM Scan', icon: Database, description: 'Analyze AI dependencies', available: false },
    { key: 'expense', name: 'Expense Feed', icon: CreditCard, description: 'Track AI tool spending', available: false },
    { key: 'gcp', name: 'Google Cloud', icon: Cloud, description: 'Monitor GCP AI services and resources', available: true },
    { key: 'aws', name: 'AWS', icon: Cloud, description: 'Monitor AWS AI services and resources', available: true },
  ];

  const handleAwsConnected = () => {
    setWizardState(prev => ({
      ...prev,
      integrations: { ...prev.integrations, aws: true }
    }));
  };

  const handleGcpConnected = () => {
    setWizardState(prev => ({
      ...prev,
      integrations: { ...prev.integrations, gcp: true }
    }));
  };

  const handleGithubConnected = () => {
    setWizardState(prev => ({
      ...prev,
      integrations: { ...prev.integrations, github: true }
    }));
  };

  const handleGithubDisconnect = () => {
    setWizardState(prev => ({
      ...prev,
      integrations: { ...prev.integrations, github: false }
    }));
  };

  return (
    <div className="space-y-6">
      <div className="grid gap-4">
        {integrations.map(({ key, name, icon: Icon, description, available }) => (
          <div 
            key={key} 
            className={`flex items-center justify-between p-4 rounded-lg ${
              available ? 'bg-background-light' : 'bg-background-light/50'
            }`}
          >
            <div className="flex items-center gap-4">
              <Icon size={24} className={`${available ? 'text-primary' : 'text-text-secondary'}`} />
              <div>
                <h3 className={`font-medium ${available ? '' : 'text-text-secondary'}`}>{name}</h3>
                <p className="text-sm text-text-secondary">{description}</p>
              </div>
            </div>
            {wizardState.integrations[key] ? (
              <button
                onClick={() => key === 'github' ? handleGithubDisconnect() : null}
                className="px-3 py-1 bg-primary/20 text-primary rounded-full text-sm hover:bg-primary/30 transition-colors"
              >
                Connected
              </button>
            ) : (
              <div className="flex items-center gap-2">
                {!available && (
                  <span className="text-xs text-text-secondary bg-background px-2 py-1 rounded">
                    Not available in closed beta
                  </span>
                )}
                <button 
                  className={`px-4 py-2 border rounded-lg transition-colors ${
                    available 
                      ? 'border-primary/20 hover:bg-primary/10' 
                      : 'border-primary/10 text-text-secondary cursor-not-allowed'
                  }`}
                  onClick={() => {
                    if (!available) return;
                    if (key === 'aws') setIsAwsDrawerOpen(true);
                    if (key === 'gcp') setIsGcpDrawerOpen(true);
                    if (key === 'github') setIsGithubModalOpen(true);
                  }}
                  disabled={!available}
                >
                  Connect
                </button>
              </div>
            )}
          </div>
        ))}
      </div>

      <button className="text-text-secondary hover:text-primary transition-colors text-sm">
        Skip integrations (you can enable later)
      </button>

      <AwsDrawer
        isOpen={isAwsDrawerOpen}
        onClose={() => setIsAwsDrawerOpen(false)}
        onConnected={handleAwsConnected}
      />

      <GcpDrawer
        isOpen={isGcpDrawerOpen}
        onClose={() => setIsGcpDrawerOpen(false)}
        onConnected={handleGcpConnected}
      />

      <GithubConnectModal
        isOpen={isGithubModalOpen}
        onClose={() => setIsGithubModalOpen(false)}
        onConnected={handleGithubConnected}
      />
    </div>
  );
}

function GovernanceStep() {
  const { wizardState, setWizardState } = useContext(WizardContext);

  const toggles = [
    { key: 'riskRegister', label: 'Risk register exists' },
    { key: 'ethicsBoard', label: 'AI ethics board' },
    { key: 'annualAudit', label: 'Annual audit' },
    { key: 'postIncidentReview', label: 'Post-incident review process' },
    { key: 'nistAiRmf', label: 'Uses NIST AI RMF' },
  ];

  return (
    <div className="space-y-4">
      {toggles.map(({ key, label }) => (
        <label key={key} className="flex items-center justify-between p-4 bg-background-light rounded-lg cursor-pointer">
          <span>{label}</span>
          <div
            className={`w-12 h-6 rounded-full transition-colors ${
              wizardState.governance[key] ? 'bg-primary' : 'bg-background'
            }`}
            onClick={() => setWizardState(prev => ({
              ...prev,
              governance: { ...prev.governance, [key]: !prev.governance[key] }
            }))}
          >
            <div
              className={`w-5 h-5 rounded-full bg-background transition-transform ${
                wizardState.governance[key] ? 'translate-x-7' : 'translate-x-0.5'
              }`}
            />
          </div>
        </label>
      ))}
    </div>
  );
}

function ReviewStep({ onRunAssessment }) {
  const { wizardState } = useContext(WizardContext);

  if (wizardState.loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <div className="animate-spin mb-4">
          <Loader size={32} className="text-primary" />
        </div>
        <h3 className="text-lg font-medium mb-4">Running Assessment...</h3>
        <div className="space-y-2 text-text-secondary">
          <p className="flex items-center gap-2">
            <CheckCircle size={16} className="text-primary" />
            Syncing feeds...
          </p>
          <p className="flex items-center gap-2">
            <CheckCircle size={16} className="text-primary" />
            Parsing docs...
          </p>
          <p className="flex items-center gap-2">
            <Loader size={16} className="animate-spin text-primary" />
            Scoring dimensions...
          </p>
        </div>
      </div>
    );
  }

  if (wizardState.result) {
    return (
      <div className="flex flex-col items-center py-12">
        <div className="text-6xl font-bold text-primary mb-8">{wizardState.result.rating}</div>
        <div className="w-full max-w-md space-y-4">
          {Object.entries(wizardState.result.dimensions).map(([key, value]) => (
            <div key={key}>
              <div className="flex justify-between text-sm mb-1">
                <span>{key}</span>
                <span>{value}%</span>
              </div>
              <div className="h-2 bg-background-light rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary rounded-full transition-all"
                  style={{ width: `${value}%` }}
                />
              </div>
            </div>
          ))}
        </div>
        <div className="flex gap-4 mt-8">
          <button
            onClick={() => console.log('View details:', wizardState)}
            className="px-4 py-2 bg-primary text-background rounded-lg hover:bg-primary-light transition-colors"
          >
            View details
          </button>
          <button className="px-4 py-2 border border-primary/20 rounded-lg hover:bg-primary/10 transition-colors">
            Download PDF
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <h3 className="font-medium">Assessment Scope</h3>
        <div className="bg-background-light rounded-lg p-4">
          <p>{wizardState.scope === 'entire' ? 'Entire organisation' : `Business unit: ${wizardState.businessUnit}`}</p>
        </div>
      </div>

      <div className="space-y-4">
        <h3 className="font-medium">Evidence Mode</h3>
        <div className="bg-background-light rounded-lg p-4">
          <p>{wizardState.evidenceMode === 'agent' ? 'Agent data only' : 'Upload extra evidence'}</p>
          {wizardState.files.length > 0 && (
            <div className="mt-2">
              <p className="text-sm text-text-secondary">Uploaded files:</p>
              <ul className="list-disc list-inside mt-1">
                {wizardState.files.map((file, index) => (
                  <li key={index}>{file.name}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      <div className="space-y-4">
        <h3 className="font-medium">Connected Integrations</h3>
        <div className="bg-background-light rounded-lg p-4">
          <ul className="space-y-2">
            {Object.entries(wizardState.integrations).map(([key, value]) => (
              <li key={key} className="flex items-center gap-2">
                {value ? (
                  <CheckCircle size={16} className="text-primary" />
                ) : (
                  <AlertCircle size={16} className="text-text-secondary" />
                )}
                {key.charAt(0).toUpperCase() + key.slice(1)}
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="space-y-4">
        <h3 className="font-medium">Governance Checklist</h3>
        <div className="bg-background-light rounded-lg p-4">
          <ul className="space-y-2">
            {Object.entries(wizardState.governance).map(([key, value]) => (
              <li key={key} className="flex items-center gap-2">
                {value ? (
                  <CheckCircle size={16} className="text-primary" />
                ) : (
                  <AlertCircle size={16} className="text-text-secondary" />
                )}
                {key
                  .replace(/([A-Z])/g, ' $1')
                  .toLowerCase()
                  .replace(/^\w/, c => c.toUpperCase())}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
} 